import {
  DeviceMobile,
  Eye,
  EyeSlash,
  Factory,
  Images,
  Ruler,
  SquaresFour,
  WarningCircle,
} from '@phosphor-icons/react';
import { Link, Outlet } from '@tanstack/react-router';
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageMenu, ThemeToggleButton } from '@/components/shell/LanguageMenu';
import { cx } from '@/components/ui/cx';
import { useTheme } from '@/hooks/usePreferences';

const POINTS = [
  { key: 'measurement', icon: Ruler },
  { key: 'photos', icon: Images },
  { key: 'production', icon: Factory },
  { key: 'projects', icon: SquaresFour },
] as const;

/** Raspored iz dizajna „Prijava“: forma lijevo, navy panel desno (auto-fit 480px kolone). */
export function AuthLayout() {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme(false);
  return (
    <div className="grid min-h-screen grid-cols-[repeat(auto-fit,minmax(min(100%,480px),1fr))] bg-surface">
      <section className="flex flex-col bg-surface px-[clamp(24px,5vw,72px)] py-7">
        <div className="flex items-center justify-between gap-4">
          <Link to="/prijava" search={{ redirect: undefined, reason: undefined }} className="flex">
            <span className="font-heading text-[22px] leading-none font-semibold tracking-[-.02em] whitespace-nowrap text-text">
              Smart Measure
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageMenu />
            <ThemeToggleButton theme={theme} onToggle={toggle} />
          </div>
        </div>
        <main className="flex flex-1 items-center py-12">
          <div className="flex w-full max-w-[420px] flex-col gap-7">
            <Outlet />
          </div>
        </main>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-muted">
          <span>{t('auth:layout.copyright', { year: new Date().getFullYear() })}</span>
          <a href="https://smartmeasure.app/privatnost" className="text-muted hover:text-accent">
            {t('auth:layout.privacy')}
          </a>
          <a href="https://smartmeasure.app/uslovi" className="text-muted hover:text-accent">
            {t('auth:layout.terms')}
          </a>
          <a href="mailto:podrska@smartmeasure.app" className="text-muted hover:text-accent">
            {t('auth:layout.support')}
          </a>
        </div>
      </section>
      <aside className="relative flex flex-col justify-between gap-10 overflow-hidden bg-navy p-[clamp(32px,5vw,72px)] text-white">
        <span className="text-[13px] font-semibold tracking-[.08em] text-[#9CC8FF] uppercase">
          {t('auth:layout.kicker')}
        </span>
        <div className="flex max-w-[520px] flex-col gap-7">
          <h2 className="m-0 font-heading text-[clamp(30px,3.4vw,44px)] leading-[1.08] font-semibold tracking-[-.03em] text-balance">
            {t('auth:layout.heading')}
          </h2>
          <ul className="m-0 grid list-none gap-4 p-0">
            {POINTS.map((p) => (
              <li key={p.key} className="grid grid-cols-[40px_minmax(0,1fr)] items-start gap-3.5">
                <span className="grid h-10 w-10 place-items-center rounded-lg border border-white/14 bg-white/8 text-[#9CC8FF]">
                  <p.icon size={19} />
                </span>
                <span className="flex flex-col gap-[3px]">
                  <span className="text-[15px] font-semibold">
                    {t(`auth:layout.points.${p.key}.title`)}
                  </span>
                  <span className="text-sm leading-normal text-[#C9D1DD]">
                    {t(`auth:layout.points.${p.key}.description`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex max-w-[520px] items-center gap-3 rounded-xl border border-white/12 bg-white/6 px-4 py-3.5 text-sm leading-normal text-[#C9D1DD]">
          <DeviceMobile size={22} className="flex-none text-[#9CC8FF]" />
          <span>{t('auth:layout.mobileNote')}</span>
        </div>
      </aside>
    </div>
  );
}

export function AuthHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="m-0 font-heading text-[34px] leading-[1.1] font-semibold tracking-[-.03em]">
        {title}
      </h1>
      {subtitle ? <p className="m-0 text-[15px] leading-[1.55] text-muted">{subtitle}</p> : null}
    </div>
  );
}

export function AuthAlert({
  children,
  tone = 'danger',
}: {
  children: ReactNode;
  tone?: 'danger' | 'ok';
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx(
        'flex items-center gap-2.5 rounded-md px-3.5 py-3 text-sm font-medium',
        tone === 'danger' ? 'bg-danger-bg text-danger' : 'bg-ok-bg text-text2',
      )}
    >
      <WarningCircle size={18} weight="fill" className="flex-none" />
      <span>{children}</span>
    </div>
  );
}

export const authInputCls =
  'min-h-[46px] w-full rounded-md border border-line2 bg-surface px-3.5 text-[15px] font-normal text-text outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--chip)] aria-[invalid=true]:border-danger';

export const AuthInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function AuthInput({ invalid, className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(authInputCls, className)}
      {...rest}
    />
  );
});

/** Polje lozinke sa dugmetom „Prikaži/Sakrij“ (oko). */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; icon?: ReactNode }
>(function PasswordInput({ invalid, icon, ...rest }, ref) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <span
      className={cx(
        'flex min-h-[46px] items-center gap-2.5 rounded-md border bg-surface pr-1.5 pl-3.5 text-muted focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--chip)]',
        invalid ? 'border-danger' : 'border-line2',
      )}
    >
      {icon}
      <input
        ref={ref}
        type={show ? 'text' : 'password'}
        aria-invalid={invalid || undefined}
        className="min-w-0 flex-1 border-0 bg-transparent text-[15px] font-normal text-text outline-none"
        {...rest}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? t('auth:password.hide') : t('auth:password.show')}
        className="grid h-9 w-9 place-items-center rounded-sm border-0 bg-transparent text-muted hover:text-text"
      >
        {show ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </span>
  );
});

export function FieldError({ msg }: { msg?: string | undefined }) {
  const { t } = useTranslation();
  if (!msg) return null;
  return (
    <span role="alert" className="text-xs font-medium text-danger">
      {t(msg.includes(':') ? msg : `errors:${msg}`)}
    </span>
  );
}

/** Skriveno polje protiv botova (honeypot): ljudi ga ne vide, botovi ga popune. */
export function Honeypot({ register }: { register: object }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: 1,
        height: 1,
        overflow: 'hidden',
      }}
    >
      <label>
        Website
        <input type="text" tabIndex={-1} autoComplete="off" {...register} />
      </label>
    </div>
  );
}
